import React, { useMemo, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueries } from '@tanstack/react-query';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Box from '@cloudscape-design/components/box';
import Input from '@cloudscape-design/components/input';
import Button from '@cloudscape-design/components/button';
import Select from '@cloudscape-design/components/select';
import Badge from '@cloudscape-design/components/badge';
import Table from '@cloudscape-design/components/table';
import SegmentedControl from '@cloudscape-design/components/segmented-control';
import Link from '@cloudscape-design/components/link';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import { getEvents, listStreams } from '../../api/events';
import { CausalityDag } from '../../components/CausalityDag';
import { EventDetailPanel } from '../../components/EventDetailPanel';
import { EventTypeIndicator } from '../../components/EventTypeIndicator';
import { AiBadge } from '../../components/AiBadge';
import { CausedByChip } from '../../components/CausedByChip';
import { resolveEventType } from '../../utils/resolveEventType';
import { isBedrockEvent } from '../../utils/isBedrockEvent';
import { formatTimestamp, getStoredTimeFormat } from '../../utils/timeFormat';
import { StreamTimeline, TimelineEvent } from './StreamTimeline';
import { EXPLORER_STORES, colorForStore } from './storeColors';

const STORE_OPTIONS = [
  { label: 'All stores', value: '' },
  ...EXPLORER_STORES.map((s) => ({ label: s, value: s })),
];

function getPreview(data: unknown): string {
  if (!data || typeof data !== 'object') return '—';
  const priority = ['analysis', 'reason', 'decision', 'status', 'amount', 'score', 'reviewOutcome'];
  const entries = Object.entries(data as Record<string, unknown>).filter(([k]) => !k.startsWith('_'));
  const first = entries.filter(([k]) => priority.includes(k)).slice(0, 2);
  const rest = entries.filter(([k]) => !priority.includes(k)).slice(0, 3 - first.length);
  const merged = first.concat(rest).slice(0, 3);
  if (merged.length === 0) return '—';
  return merged
    .map(([k, v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v).slice(0, 40) : String(v).slice(0, 60)}`)
    .join(' · ');
}

interface StreamExplorerProps {
  /** Callback when the user clicks "Query this" — parent switches to Query mode. */
  onQueryThis?: (esql: string) => void;
}

/**
 * Cross-store stream browser. Given a stream key, queries every target store
 * in parallel and merges the results into a chronologically-ordered feed with
 * Timeline / Graph / Table views. Selected event opens the shared
 * EventDetailPanel to preserve linkability (event type -> schema, domain ->
 * store, causedBy -> lineage, all already implemented in Stage 6).
 *
 * URL state (managed via useSearchParams):
 *   stream : the paymentId / stream key currently loaded
 *   store  : optional filter (single store name; empty = all stores)
 *   view   : timeline (default) | graph | table
 *   focus  : eventId to pre-select (opens the detail panel + centers graph)
 */
export function StreamExplorer({ onQueryThis }: StreamExplorerProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlStream = searchParams.get('stream') || '';
  const urlStore = searchParams.get('store') || '';
  const urlView = searchParams.get('view') || 'timeline';
  const urlFocus = searchParams.get('focus') || '';

  const [streamInput, setStreamInput] = useState(urlStream);
  useEffect(() => { setStreamInput(urlStream); }, [urlStream]);

  const activeStores = urlStore ? [urlStore] : (EXPLORER_STORES as unknown as string[]);

  const queries = useQueries({
    queries: activeStores.map((store) => ({
      queryKey: ['explorer-events', store, urlStream],
      queryFn: async () => {
        if (!urlStream) return { events: [] as TimelineEvent[] };
        const resp = await getEvents(store, urlStream, { limit: 100, sort: 'asc' });
        const raw = (resp?.data as { events?: any[] } | undefined)?.events || [];
        return { events: raw.map((e: any) => ({ ...e, _sourceStore: store })) as TimelineEvent[] };
      },
      enabled: !!urlStream,
      staleTime: 5000,
    })),
  });

  const isLoading = queries.some((q) => q.isLoading);
  const mergedEvents = useMemo<TimelineEvent[]>(() => {
    const all: TimelineEvent[] = [];
    for (const q of queries) {
      const data = q.data as { events?: TimelineEvent[] } | undefined;
      if (data?.events) all.push(...data.events);
    }
    return all
      .filter((e) => {
        // Prefer resolveEventType so legacy metadata.eventType='unknown'
        // events with data.sourceEventType still surface with their real
        // type. resolveEventType returns '—' when there's nothing usable.
        const resolved = resolveEventType(e);
        return !!resolved && resolved !== '—' && resolved !== '-';
      })
      .sort((a, b) => {
        const ta = a.metadata?.timestamp || '';
        const tb = b.metadata?.timestamp || '';
        return String(ta).localeCompare(String(tb));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queries.map((q) => q.dataUpdatedAt).join('|'), urlStream, urlStore]);

  const storeMap = useMemo(() => {
    const m: Record<string, string> = {};
    for (const e of mergedEvents) {
      const id = e.metadata?.eventId;
      if (id && e._sourceStore) m[id] = e._sourceStore;
    }
    return m;
  }, [mergedEvents]);

  const selectedEvent = useMemo(
    () => mergedEvents.find((e) => e.metadata?.eventId === urlFocus) || null,
    [mergedEvents, urlFocus]
  );

  const runSearch = (streamValue: string, storeValue?: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('mode', 'stream');
    if (streamValue) next.set('stream', streamValue); else next.delete('stream');
    if (storeValue !== undefined) {
      if (storeValue) next.set('store', storeValue); else next.delete('store');
    }
    next.delete('focus');
    setSearchParams(next, { replace: false });
  };

  const setView = (v: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('view', v);
    setSearchParams(next, { replace: true });
  };

  const setFocus = (evt: TimelineEvent | null) => {
    const next = new URLSearchParams(searchParams);
    if (evt?.metadata?.eventId) next.set('focus', evt.metadata.eventId);
    else next.delete('focus');
    setSearchParams(next, { replace: true });
  };

  const handleQueryThis = () => {
    if (!urlStream || !onQueryThis) return;
    const stores = urlStore
      ? `"${urlStore}"`
      : EXPLORER_STORES.map((s) => `"${s}"`).join(', ');
    const esql = `SELECT * FROM ${stores} WHERE stream = '${urlStream}' ORDER BY metadata.timestamp ASC LIMIT 200`;
    onQueryThis(esql);
  };

  return (
    <SpaceBetween size="l">
      <Container>
        <SpaceBetween size="m">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 200px auto auto', gap: 8, alignItems: 'end' }}>
            <div>
              <Box variant="awsui-key-label">Stream key</Box>
              <Input
                value={streamInput}
                onChange={({ detail }) => setStreamInput(detail.value)}
                onKeyDown={(e) => { if (e.detail.key === 'Enter') runSearch(streamInput, urlStore); }}
                placeholder="e.g. pay-mrfcgqnj-2lrhtx"
              />
            </div>
            <div>
              <Box variant="awsui-key-label">Store filter</Box>
              <Select
                selectedOption={STORE_OPTIONS.find((o) => o.value === urlStore) || STORE_OPTIONS[0]}
                onChange={({ detail }) => runSearch(streamInput, detail.selectedOption.value || '')}
                options={STORE_OPTIONS}
              />
            </div>
            <Button variant="primary" onClick={() => runSearch(streamInput, urlStore)}>Search</Button>
            {urlStream && onQueryThis && (
              <Button onClick={handleQueryThis} iconName="external" iconAlign="right">
                Query this
              </Button>
            )}
          </div>
          {urlStream && (
            <Box color="text-body-secondary" fontSize="body-s">
              Showing events from {activeStores.join(', ')} where <code>stream = {urlStream}</code>.{' '}
              {mergedEvents.length} event{mergedEvents.length === 1 ? '' : 's'} found.
            </Box>
          )}
        </SpaceBetween>
      </Container>

      {!urlStream ? (
        <RecentStreams onPick={(streamValue, store) => runSearch(streamValue, store)} />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: selectedEvent ? '1fr 380px' : '1fr', gap: 16 }}>
          <Container
            header={
              <Header
                variant="h3"
                actions={
                  <SegmentedControl
                    selectedId={urlView}
                    onChange={({ detail }) => setView(detail.selectedId)}
                    options={[
                      { id: 'timeline', text: 'Timeline' },
                      { id: 'graph', text: 'Graph' },
                      { id: 'table', text: 'Table' },
                    ]}
                  />
                }
              >
                Events {isLoading ? '(loading…)' : `(${mergedEvents.length})`}
              </Header>
            }
          >
            {urlView === 'timeline' && (
              <StreamTimeline
                events={mergedEvents}
                selectedEventId={urlFocus || undefined}
                onSelect={setFocus}
              />
            )}
            {urlView === 'graph' && (
              <CausalityDag
                // CausalityDag's internal EventItem doesn't allow `data: null`
                // and doesn't declare _sourceStore — TimelineEvent widens both,
                // so cast through unknown for the structural assignment.
                events={mergedEvents as unknown as Parameters<typeof CausalityDag>[0]['events']}
                storeMap={storeMap}
                height={520}
              />
            )}
            {urlView === 'table' && (
              <StreamTable
                events={mergedEvents}
                selectedEventId={urlFocus || undefined}
                onSelect={setFocus}
              />
            )}
          </Container>
          {selectedEvent && (
            <div style={{ position: 'sticky', top: 0, alignSelf: 'start' }}>
              <EventDetailPanel
                event={selectedEvent as Record<string, unknown>}
                storeName={selectedEvent._sourceStore || ''}
                onClose={() => setFocus(null)}
              />
            </div>
          )}
        </div>
      )}
    </SpaceBetween>
  );
}

// -----------------------------------------------------------------------------
// Recent Streams empty-state panel
// -----------------------------------------------------------------------------

function RecentStreams({ onPick }: { onPick: (stream: string, store: string) => void }) {
  const queries = useQueries({
    queries: EXPLORER_STORES.map((store) => ({
      queryKey: ['explorer-streams', store],
      queryFn: async () => {
        const resp = await listStreams(store);
        return { streams: (resp?.streams || []).slice(0, 5), store };
      },
      staleTime: 60_000,
    })),
  });

  return (
    <Container header={<Header variant="h3" description="Pick a recent stream to explore its lifecycle">Recent streams</Header>}>
      <ColumnLayout columns={EXPLORER_STORES.length} variant="text-grid">
        {queries.map((q, i) => {
          const store = EXPLORER_STORES[i];
          const data = q.data as { streams?: string[] } | undefined;
          const streams = data?.streams || [];
          return (
            <div key={store}>
              <Box variant="awsui-key-label">
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 10, height: 10, borderRadius: 5, background: colorForStore(store), display: 'inline-block' }} />
                  {store}
                </span>
              </Box>
              {q.isLoading && <Box color="text-body-secondary">Loading…</Box>}
              {!q.isLoading && streams.length === 0 && <Box color="text-body-secondary">No streams</Box>}
              <SpaceBetween size="xxs">
                {streams.map((s) => (
                  <Link
                    key={s}
                    fontSize="body-s"
                    onFollow={(e) => { e.preventDefault(); onPick(s, ''); }}
                  >
                    <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{s}</span>
                  </Link>
                ))}
              </SpaceBetween>
            </div>
          );
        })}
      </ColumnLayout>
    </Container>
  );
}

// -----------------------------------------------------------------------------
// Table view (Store column always visible)
// -----------------------------------------------------------------------------

function StreamTable({
  events, selectedEventId, onSelect,
}: {
  events: TimelineEvent[];
  selectedEventId?: string;
  onSelect: (e: TimelineEvent) => void;
}) {
  const timeFormat = getStoredTimeFormat();
  return (
    <div style={{ overflowX: 'auto' }}>
      <Table
        variant="embedded"
        items={events}
        trackBy={(e) => e.metadata?.eventId || String(e.pk) + String(e.metadata?.timestamp)}
        onRowClick={({ detail }) => onSelect(detail.item as TimelineEvent)}
        selectionType="single"
        selectedItems={
          selectedEventId
            ? events.filter((e) => e.metadata?.eventId === selectedEventId)
            : []
        }
        columnDefinitions={[
          {
            id: 'store',
            header: 'Store',
            cell: (item: TimelineEvent) => (
              <Badge color={item._sourceStore === 'alerts' ? 'red' : 'blue'}>
                <span style={{
                  display: 'inline-block', width: 8, height: 8, borderRadius: 4,
                  background: colorForStore(item._sourceStore || ''), marginRight: 4,
                  verticalAlign: 'middle',
                }} />
                {item._sourceStore}
              </Badge>
            ),
            width: 170,
          },
          {
            id: 'version',
            header: 'Version',
            cell: (item: TimelineEvent) => {
              const md = item.metadata || {};
              return (md.version as number) ?? (md.sequence as number) ?? '—';
            },
            width: 80,
          },
          {
            id: 'eventType',
            header: 'Event type',
            cell: (item: TimelineEvent) => (
              <>
                <EventTypeIndicator eventType={resolveEventType(item)} />
                {isBedrockEvent(item) && <AiBadge />}
              </>
            ),
            width: 200,
          },
          {
            id: 'preview',
            header: 'Preview',
            cell: (item: TimelineEvent) => (
              <span style={{
                display: 'inline-block', maxWidth: '100%',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                color: '#687078', fontSize: 13,
              }} title={getPreview(item.data)}>
                {getPreview(item.data)}
              </span>
            ),
            width: 320,
            minWidth: 120,
          },
          {
            id: 'causedBy',
            header: 'Caused by',
            cell: (item: TimelineEvent) => (
              <CausedByChip
                causedBy={item.metadata?.causedBy as string | string[] | null | undefined}
                storeName={item._sourceStore}
              />
            ),
            width: 130,
          },
          {
            id: 'time',
            header: 'Time',
            cell: (item: TimelineEvent) => formatTimestamp(item.metadata?.timestamp, timeFormat),
            width: 130,
          },
        ]}
        empty={<Box textAlign="center" color="text-body-secondary">No events</Box>}
      />
    </div>
  );
}
